import { Component, inject, input, output, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PublicI18nService } from '../../services/public-i18n.service';
import {
  PublicOfferDetails,
  AcceptOfferRequest,
  DeclineOfferRequest,
  OfferPageSettings,
  OfferPageContent,
  DEFAULT_OFFER_PAGE_SETTINGS,
} from '@frontend/data-access';

export type OfferViewState = 'form' | 'accepted' | 'declined' | 'expired' | 'already-accepted' | 'request-closed';

@Component({
  selector: 'pc-offer-acceptance',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  styles: [`
    :host {
      display: block;
    }

    /* Container */
    .offer-container {
      max-width: 40rem;
      margin: 0 auto;
    }

    /* Cards */
    .offer-card {
      border-radius: var(--radius, 0.75rem);
      border: 1px solid rgb(var(--color-border, 229 231 235));
      background-color: rgb(var(--color-surface, 255 255 255));
      overflow: hidden;
      margin-bottom: 1.25rem;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 1px 2px -1px rgba(0, 0, 0, 0.04);
    }

    /* Card header with title */
    .card-header {
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid rgb(var(--color-border, 229 231 235));
      background-color: rgb(var(--color-surface, 249 250 251));
    }
    .card-body {
      padding: 1.5rem;
    }

    /* Offer title area */
    .offer-title {
      font-size: 1.125rem;
      font-weight: 600;
      margin: 0;
      color: rgb(var(--color-foreground, 30 41 59));
    }

    /* Section titles */
    .section-title {
      font-size: 0.8125rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin: 0 0 1rem;
      color: rgb(var(--color-muted, 100 116 139));
    }

    /* Detail rows */
    .detail-grid {
      display: grid;
      gap: 0;
    }
    .detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid rgb(var(--color-border, 241 245 249));
    }
    .detail-row:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    .detail-row:first-child {
      padding-top: 0;
    }
    .detail-label {
      font-size: 0.875rem;
      color: rgb(var(--color-muted, 100 116 139));
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
    .detail-label svg {
      width: 1rem;
      height: 1rem;
      opacity: 0.6;
    }
    .detail-value {
      font-size: 0.875rem;
      font-weight: 600;
      color: rgb(var(--color-foreground, 30 41 59));
      text-align: right;
    }
    .detail-value.price {
      font-size: 1rem;
      color: rgb(var(--color-primary, 59 130 246));
    }

    /* Provider message */
    .provider-message {
      margin-top: 0.25rem;
      padding: 1rem 1.25rem;
      border-radius: 0.625rem;
      background-color: color-mix(in srgb, rgb(var(--color-primary, 59 130 246)) 6%, transparent);
      border-left: 3px solid rgb(var(--color-primary, 59 130 246));
    }
    .provider-message-label {
      font-size: 0.6875rem;
      font-weight: 700;
      color: rgb(var(--color-primary, 59 130 246));
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 0.375rem;
    }
    .provider-message-text {
      font-size: 0.875rem;
      color: rgb(var(--color-foreground, 30 41 59));
      white-space: pre-wrap;
      line-height: 1.5;
      margin: 0;
    }

    /* Expiry badge */
    .expiry-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      border-radius: 9999px;
      font-size: 0.8125rem;
      font-weight: 600;
      background-color: #fef3c7;
      color: #92400e;
      margin-bottom: 1.25rem;
    }

    /* Form elements */
    .form-group {
      margin-bottom: 1rem;
    }
    .form-row {
      display: grid;
      grid-template-columns: 3fr 1fr;
      gap: 0.875rem;
    }
    .form-row-equal {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.875rem;
    }
    .form-label {
      display: block;
      font-size: 0.8125rem;
      font-weight: 500;
      margin-bottom: 0.375rem;
      color: rgb(var(--color-foreground, 30 41 59));
    }
    .form-input {
      width: 100%;
      padding: 0.625rem 0.875rem;
      font-size: 0.875rem;
      border-radius: 0.5rem;
      border: 1px solid rgb(var(--color-border, 226 232 240));
      background-color: rgb(var(--color-surface, 255 255 255));
      color: rgb(var(--color-foreground, 30 41 59));
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      outline: none;
    }
    .form-input::placeholder {
      color: rgb(var(--color-muted, 148 163 184));
    }
    .form-input:focus {
      border-color: rgb(var(--color-primary, 59 130 246));
      box-shadow: 0 0 0 3px color-mix(in srgb, rgb(var(--color-primary, 59 130 246)) 12%, transparent);
    }
    .form-input.ng-invalid.ng-touched {
      border-color: #ef4444;
    }
    .form-error {
      font-size: 0.75rem;
      color: #ef4444;
      margin-top: 0.25rem;
    }

    /* Checkbox elements */
    .checkbox-group {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
    }
    .checkbox-input {
      width: 1.125rem;
      height: 1.125rem;
      margin-top: 0.125rem;
      accent-color: rgb(var(--color-primary, 59 130 246));
      cursor: pointer;
      flex-shrink: 0;
    }
    .checkbox-label {
      font-size: 0.8125rem;
      color: rgb(var(--color-foreground, 30 41 59));
      cursor: pointer;
      line-height: 1.5;
    }
    .checkbox-label a {
      color: rgb(var(--color-primary, 59 130 246));
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    /* Buttons */
    .btn-primary {
      width: 100%;
      padding: 0.875rem 1.5rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 0.5rem;
      border: none;
      background-color: rgb(var(--color-primary, 59 130 246));
      color: #fff;
      cursor: pointer;
      transition: all 0.15s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
    }
    .btn-primary:hover:not(:disabled) {
      filter: brightness(1.08);
      box-shadow: 0 4px 12px color-mix(in srgb, rgb(var(--color-primary, 59 130 246)) 30%, transparent);
    }
    .btn-primary:active:not(:disabled) {
      transform: scale(0.98);
    }
    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .btn-secondary {
      width: 100%;
      padding: 0.75rem 1.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      border-radius: 0.5rem;
      border: 1px solid rgb(var(--color-border, 226 232 240));
      background-color: transparent;
      color: rgb(var(--color-foreground, 30 41 59));
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .btn-secondary:hover:not(:disabled) {
      background-color: rgb(var(--color-surface, 248 250 252));
    }
    .btn-secondary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    .btn-danger {
      border-color: #fca5a5;
      color: #dc2626;
    }
    .btn-danger:hover:not(:disabled) {
      background-color: #fef2f2;
    }

    /* Decline section */
    .decline-section {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid rgb(var(--color-border, 226 232 240));
    }
    .decline-toggle {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8125rem;
      color: rgb(var(--color-muted, 100 116 139));
      cursor: pointer;
      background: none;
      border: none;
      padding: 0.25rem 0;
      transition: color 0.15s ease;
    }
    .decline-toggle:hover {
      color: #ef4444;
    }
    .decline-toggle svg {
      transition: transform 0.15s ease;
    }
    .decline-form {
      margin-top: 1rem;
    }

    /* Status cards */
    .status-card {
      text-align: center;
      padding: 3rem 2rem;
    }
    .status-icon {
      width: 4rem;
      height: 4rem;
      margin: 0 auto 1.25rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .status-icon-success {
      background-color: #dcfce7;
      color: #16a34a;
    }
    .status-icon-warning {
      background-color: #fef3c7;
      color: #d97706;
    }
    .status-icon-error {
      background-color: #fee2e2;
      color: #dc2626;
    }
    .status-title {
      font-size: 1.25rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: rgb(var(--color-foreground, 30 41 59));
    }
    .status-message {
      font-size: 0.875rem;
      color: rgb(var(--color-muted, 100 116 139));
      line-height: 1.5;
    }

    /* Error alert */
    .error-alert {
      margin-bottom: 1rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      background-color: #fef2f2;
      border: 1px solid #fecaca;
      color: #dc2626;
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    /* Spinner */
    .spinner {
      display: inline-block;
      width: 1.125rem;
      height: 1.125rem;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
    .spinner-dark {
      border: 2px solid rgba(0, 0, 0, 0.1);
      border-top-color: rgb(var(--color-foreground, 30 41 59));
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    /* Responsive */
    @media (max-width: 640px) {
      .card-body {
        padding: 1.25rem;
      }
      .card-header {
        padding: 1rem 1.25rem;
      }
      .form-row,
      .form-row-equal {
        grid-template-columns: 1fr;
        gap: 1rem;
      }
    }
  `],
  template: `
    <div class="offer-container">
      <!-- Status views for non-actionable states -->
      @if (viewState() === 'accepted') {
        <div class="offer-card status-card">
          <div class="status-icon status-icon-success">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h2 class="status-title">{{ i18n.t('offer.statusAccepted') }}</h2>
          <p class="status-message">{{ i18n.t('offer.statusAcceptedMessage') }}</p>
        </div>
      }

      @if (viewState() === 'declined') {
        <div class="offer-card status-card">
          <div class="status-icon status-icon-warning">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </div>
          <h2 class="status-title">{{ i18n.t('offer.statusDeclined') }}</h2>
          <p class="status-message">{{ i18n.t('offer.statusDeclinedMessage') }}</p>
        </div>
      }

      @if (viewState() === 'expired') {
        <div class="offer-card status-card">
          <div class="status-icon status-icon-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <h2 class="status-title">{{ i18n.t('offer.statusExpired') }}</h2>
          <p class="status-message">{{ i18n.t('offer.statusExpiredMessage') }}</p>
        </div>
      }

      @if (viewState() === 'already-accepted') {
        <div class="offer-card status-card">
          <div class="status-icon status-icon-success">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <h2 class="status-title">{{ i18n.t('offer.statusAlreadyAccepted') }}</h2>
          <p class="status-message">{{ i18n.t('offer.statusAlreadyAcceptedMessage') }}</p>
        </div>
      }

      @if (viewState() === 'request-closed') {
        <div class="offer-card status-card">
          <div class="status-icon status-icon-warning">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <h2 class="status-title">{{ i18n.t('offer.requestClosed') }}</h2>
          <p class="status-message">{{ i18n.t('offer.requestClosedMessage') }}</p>
        </div>
      }

      <!-- Actionable form view -->
      @if (viewState() === 'form' && offer()) {
        <!-- Offer details card -->
        <div class="offer-card">
          <div class="card-header">
            <h1 class="offer-title">{{ getText('pageTitle', 'offer.title') }}</h1>
          </div>
          <div class="card-body">
            <!-- Expiry warning -->
            @if (hoursUntilExpiry() !== null && hoursUntilExpiry()! < 24) {
              <div class="expiry-badge">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                {{ i18n.t('offer.expiresIn') }} {{ hoursUntilExpiry() }} {{ i18n.t('offer.hours') }}
              </div>
            }

            <p class="section-title">{{ i18n.t('offer.appointmentDetails') }}</p>

            <div class="detail-grid">
              <div class="detail-row">
                <span class="detail-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                  {{ i18n.t('offer.date') }}
                </span>
                <span class="detail-value">{{ offer()!.appointmentDateFormatted }}</span>
              </div>

              <div class="detail-row">
                <span class="detail-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                  {{ i18n.t('offer.time') }}
                </span>
                <span class="detail-value">{{ offer()!.appointmentStartLocal }} - {{ offer()!.appointmentEndLocal }}</span>
              </div>

              @if (offer()!.estimatedDurationMinutes) {
                <div class="detail-row">
                  <span class="detail-label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>
                    {{ i18n.t('offer.duration') }}
                  </span>
                  <span class="detail-value">{{ offer()!.estimatedDurationMinutes }} {{ i18n.t('offer.durationMinutes') }}</span>
                </div>
              }

              @if (offer()!.estimatedPriceEur !== null) {
                <div class="detail-row">
                  <span class="detail-label">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                    {{ i18n.t('offer.estimatedPrice') }}
                  </span>
                  <span class="detail-value price">{{ offer()!.estimatedPriceEur | currency:'EUR':'symbol':'1.2-2':i18n.getDateLocale() }}</span>
                </div>
              }

              <div class="detail-row">
                <span class="detail-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                  {{ i18n.t('offer.trade') }}
                </span>
                <span class="detail-value">{{ offer()!.tradeName }}</span>
              </div>
            </div>

            @if (offer()!.message) {
              <div class="provider-message">
                <p class="provider-message-label">{{ i18n.t('offer.message') }}</p>
                <p class="provider-message-text">{{ offer()!.message }}</p>
              </div>
            }
          </div>
        </div>

        <!-- Acceptance form card -->
        <div class="offer-card">
          <div class="card-header">
            <p class="section-title" style="margin: 0;">{{ i18n.t('offer.yourDetails') }}</p>
          </div>
          <div class="card-body">
            <form [formGroup]="form" (ngSubmit)="onAccept()">
              <!-- Name (prefilled from request if available) -->
              <div class="form-group">
                <label for="customerName" class="form-label">
                  {{ i18n.t('offer.customerName') }} <span style="color: #ef4444;">*</span>
                </label>
                <input
                  id="customerName"
                  type="text"
                  formControlName="customerName"
                  class="form-input"
                  [placeholder]="i18n.t('offer.customerNamePlaceholder')"
                />
                @if (form.get('customerName')?.touched && form.get('customerName')?.errors?.['required']) {
                  <p class="form-error">{{ i18n.t('offer.customerNameRequired') }}</p>
                }
              </div>

              <!-- Street and house number -->
              @if (isFieldVisible('street') || isFieldVisible('houseNumber')) {
                <div class="form-row form-group">
                  @if (isFieldVisible('street')) {
                    <div>
                      <label for="street" class="form-label">
                        {{ i18n.t('offer.street') }}
                        @if (resolvedSettings().formFields['street']?.required) { <span style="color: #ef4444;">*</span> }
                      </label>
                      <input
                        id="street"
                        type="text"
                        formControlName="street"
                        class="form-input"
                        [placeholder]="i18n.t('offer.streetPlaceholder')"
                      />
                      @if (form.get('street')?.touched && form.get('street')?.errors?.['required']) {
                        <p class="form-error">{{ i18n.t('offer.streetRequired') }}</p>
                      }
                    </div>
                  }
                  @if (isFieldVisible('houseNumber')) {
                    <div>
                      <label for="houseNumber" class="form-label">
                        {{ i18n.t('offer.houseNumber') }}
                        @if (resolvedSettings().formFields['houseNumber']?.required) { <span style="color: #ef4444;">*</span> }
                      </label>
                      <input
                        id="houseNumber"
                        type="text"
                        formControlName="houseNumber"
                        class="form-input"
                        [placeholder]="i18n.t('offer.houseNumberPlaceholder')"
                      />
                      @if (form.get('houseNumber')?.touched && form.get('houseNumber')?.errors?.['required']) {
                        <p class="form-error">{{ i18n.t('offer.houseNumberRequired') }}</p>
                      }
                    </div>
                  }
                </div>
              }

              <!-- City and postal code -->
              @if (isFieldVisible('postalCode') || isFieldVisible('city')) {
                <div class="form-row-equal form-group">
                  @if (isFieldVisible('postalCode')) {
                    <div>
                      <label for="postalCode" class="form-label">
                        {{ i18n.t('offer.postalCode') }}
                        @if (resolvedSettings().formFields['postalCode']?.required) { <span style="color: #ef4444;">*</span> }
                      </label>
                      <input
                        id="postalCode"
                        type="text"
                        formControlName="postalCode"
                        class="form-input"
                        [placeholder]="i18n.t('offer.postalCodePlaceholder')"
                      />
                      @if (form.get('postalCode')?.touched && form.get('postalCode')?.errors?.['required']) {
                        <p class="form-error">{{ i18n.t('offer.postalCodeRequired') }}</p>
                      }
                    </div>
                  }
                  @if (isFieldVisible('city')) {
                    <div>
                      <label for="city" class="form-label">
                        {{ i18n.t('offer.city') }}
                        @if (resolvedSettings().formFields['city']?.required) { <span style="color: #ef4444;">*</span> }
                      </label>
                      <input
                        id="city"
                        type="text"
                        formControlName="city"
                        class="form-input"
                        [placeholder]="i18n.t('offer.cityPlaceholder')"
                      />
                      @if (form.get('city')?.touched && form.get('city')?.errors?.['required']) {
                        <p class="form-error">{{ i18n.t('offer.cityRequired') }}</p>
                      }
                    </div>
                  }
                </div>
              }

              <!-- Phone -->
              @if (isFieldVisible('phoneNumber')) {
                <div class="form-group">
                  <label for="phone" class="form-label">
                    {{ i18n.t('offer.phone') }}
                    @if (resolvedSettings().formFields['phoneNumber']?.required) { <span style="color: #ef4444;">*</span> }
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    formControlName="phoneNumber"
                    class="form-input"
                    [placeholder]="i18n.t('offer.phonePlaceholder')"
                  />
                </div>
              }

              <!-- Consent checkboxes -->
              <div class="form-group" style="margin-top: 1.5rem;">
                <div class="checkbox-group">
                  <input
                    type="checkbox"
                    id="acceptsTerms"
                    formControlName="acceptsTerms"
                    class="checkbox-input"
                  />
                  <label for="acceptsTerms" class="checkbox-label">
                    {{ i18n.t('offer.termsConsent') }}
                    @if (termsUrl()) {
                      (<a [href]="termsUrl()" target="_blank" rel="noopener" class="terms-link">{{ i18n.t('offer.viewTerms') }}</a>)
                    }
                    <span style="color: #ef4444;">*</span>
                  </label>
                </div>
                @if (form.get('acceptsTerms')?.touched && form.get('acceptsTerms')?.errors?.['requiredTrue']) {
                  <p class="form-error" style="margin-left: 1.875rem;">{{ i18n.t('offer.termsRequired') }}</p>
                }

                @if (offer()!.hasProviderPrivacyPolicy) {
                  <div class="checkbox-group">
                    <input
                      type="checkbox"
                      id="acceptsProviderPrivacy"
                      formControlName="acceptsProviderPrivacy"
                      class="checkbox-input"
                    />
                    <label for="acceptsProviderPrivacy" class="checkbox-label">
                      {{ i18n.t('offer.privacyConsent') }}
                      @if (offer()!.providerPrivacyPolicyUrl) {
                        (<a [href]="offer()!.providerPrivacyPolicyUrl" target="_blank" rel="noopener">{{ i18n.t('offer.viewPrivacyPolicy') }}</a>)
                      }
                      <span style="color: #ef4444;">*</span>
                    </label>
                  </div>
                  @if (form.get('acceptsProviderPrivacy')?.touched && form.get('acceptsProviderPrivacy')?.errors?.['requiredTrue']) {
                    <p class="form-error" style="margin-left: 1.875rem;">{{ i18n.t('offer.privacyRequired') }}</p>
                  }
                }
              </div>

              <!-- Error message -->
              @if (errorMessage()) {
                <div class="error-alert">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {{ errorMessage() }}
                </div>
              }

              <!-- Accept button -->
              <button
                type="submit"
                class="btn-primary"
                [disabled]="!form.valid || accepting()"
                style="margin-top: 1rem;"
              >
                @if (accepting()) {
                  <span class="spinner"></span>
                  {{ i18n.t('offer.accepting') }}
                } @else {
                  {{ getText('acceptButtonText', 'offer.accept') }}
                }
              </button>
            </form>

            <!-- Decline section -->
            <div class="decline-section">
              @if (!showDeclineForm()) {
                <button
                  type="button"
                  class="decline-toggle"
                  (click)="showDeclineForm.set(true)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                  {{ i18n.t('offer.decline') }}
                </button>
              } @else {
                <div class="decline-form">
                  <label for="declineReason" class="form-label">
                    {{ i18n.t('offer.declineReason') }}
                  </label>
                  <textarea
                    id="declineReason"
                    [(ngModel)]="declineReason"
                    rows="3"
                    class="form-input"
                    style="resize: none; margin-bottom: 1rem;"
                    [placeholder]="i18n.t('offer.declineReasonPlaceholder')"
                  ></textarea>
                  <button
                    type="button"
                    class="btn-secondary btn-danger"
                    [disabled]="declining()"
                    (click)="onDecline()"
                  >
                    @if (declining()) {
                      <span style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
                        <span class="spinner spinner-dark"></span>
                        {{ i18n.t('offer.declining') }}
                      </span>
                    } @else {
                      {{ i18n.t('offer.decline') }}
                    }
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class OfferAcceptanceComponent {
  readonly i18n = inject(PublicI18nService);

  /** The offer details to display */
  offer = input.required<PublicOfferDetails | null>();

  /** Whether the accept request is in progress */
  accepting = input<boolean>(false);

  /** Whether the decline request is in progress */
  declining = input<boolean>(false);

  /** Error message to display */
  errorMessage = input<string | null>(null);

  /** Offer page settings from provider configuration */
  pageSettings = input<OfferPageSettings | null>(null);

  /** Offer page content (localized) from provider configuration */
  pageContent = input<OfferPageContent | null>(null);

  /** URL for terms/privacy link */
  termsUrl = input<string | null>(null);

  /** Emitted when user accepts the offer */
  acceptOffer = output<AcceptOfferRequest>();

  /** Emitted when user declines the offer */
  declineOffer = output<DeclineOfferRequest>();

  /** Whether to show the decline form */
  showDeclineForm = signal<boolean>(false);

  /** Decline reason text */
  declineReason = '';

  form: FormGroup;

  /** Get resolved settings (custom or defaults) */
  readonly resolvedSettings = computed(() => {
    return this.pageSettings() ?? DEFAULT_OFFER_PAGE_SETTINGS;
  });

  /** Check if a form field is visible based on config */
  isFieldVisible(key: string): boolean {
    const settings = this.resolvedSettings();
    return settings.formFields[key]?.visible ?? true;
  }

  /** Get custom text with fallback to i18n key */
  getText(contentKey: keyof OfferPageContent, i18nKey: string): string {
    const content = this.pageContent();
    if (content) {
      const value = content[contentKey];
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
    return this.i18n.t(i18nKey);
  }

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      customerName: ['', [Validators.required, Validators.minLength(2)]],
      street: ['', [Validators.required]],
      houseNumber: ['', [Validators.required]],
      city: ['', [Validators.required]],
      postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      phoneNumber: [''],
      acceptsTerms: [false, [Validators.requiredTrue]],
      acceptsProviderPrivacy: [false],
    });

    // Reactively update form validators when pageSettings changes
    effect(() => {
      const settings = this.resolvedSettings();
      const addressFields = ['street', 'houseNumber', 'postalCode', 'city'];
      for (const field of addressFields) {
        const control = this.form.get(field);
        if (!control) continue;
        const config = settings.formFields[field];
        if (config && !config.visible) {
          control.clearValidators();
        } else if (config?.required) {
          control.setValidators(field === 'postalCode' ? [Validators.required, Validators.pattern(/^\d{5}$/)] : [Validators.required]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }

      const phoneControl = this.form.get('phoneNumber');
      if (phoneControl) {
        const phoneConfig = settings.formFields['phoneNumber'];
        if (phoneConfig?.required) {
          phoneControl.setValidators([Validators.required]);
        } else {
          phoneControl.clearValidators();
        }
        phoneControl.updateValueAndValidity();
      }
    });
  }

  /**
   * Determines the current view state based on offer status.
   */
  viewState = computed<OfferViewState>(() => {
    const offerData = this.offer();
    if (!offerData) {
      return 'form';
    }

    if (offerData.requestClosed) {
      return 'request-closed';
    }

    switch (offerData.status) {
      case 'Accepted':
        return 'already-accepted';
      case 'Rejected':
        return 'declined';
      case 'Expired':
        return 'expired';
      case 'Sent':
      default:
        return offerData.canAccept ? 'form' : 'request-closed';
    }
  });

  /**
   * Calculate hours until expiry for display.
   */
  hoursUntilExpiry = computed<number | null>(() => {
    const offerData = this.offer();
    if (!offerData?.expiresAtUtc) return null;

    const expiresAt = new Date(offerData.expiresAtUtc);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();

    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / (1000 * 60 * 60));
  });

  /**
   * Initialize form with customer name if available from offer.
   */
  ngOnInit(): void {
    // If offer has customer name prefilled, set it
    const offerData = this.offer();
    if (offerData?.customerName) {
      this.form.patchValue({ customerName: offerData.customerName });
    }

    // Add privacy validation if provider has privacy policy
    if (offerData?.hasProviderPrivacyPolicy) {
      this.form.get('acceptsProviderPrivacy')?.setValidators([Validators.requiredTrue]);
      this.form.get('acceptsProviderPrivacy')?.updateValueAndValidity();
    }
  }

  /**
   * Handle accept button click.
   */
  onAccept(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;

    const street = formValue.street.trim();
    const houseNumber = formValue.houseNumber?.trim();
    const fullStreet = houseNumber ? `${street} ${houseNumber}` : street;

    const request: AcceptOfferRequest = {
      customerStreet: fullStreet,
      customerCity: formValue.city.trim(),
      customerPostalCode: formValue.postalCode.trim(),
      customerPhone: formValue.phoneNumber?.trim() || null,
      privacyPolicyAccepted: formValue.acceptsTerms,
      consentPolicySource: formValue.acceptsProviderPrivacy ? 'Provider' : 'Platform',
    };

    this.acceptOffer.emit(request);
  }

  /**
   * Handle decline button click.
   */
  onDecline(): void {
    const request: DeclineOfferRequest = {
      reason: this.declineReason.trim() || null,
    };

    this.declineOffer.emit(request);
  }
}
